import type { FxQualityTier } from "./fxRuntime";
import type { FxVideoOverlayBudgetMode } from "./fxOverlayPlaybackBudget";
import type { TimelineSyncDiagnosticsSnapshot } from "../editor2/timelineSyncDiagnostics";

const DEBUG_KEY = "vb_debug_fx_fps";

type FxFpsTelemetrySample = {
  frameMs: number;
  qualityTier: FxQualityTier;
  activeSegmentationTargets: number;
  activeGpuOverlayTargets?: number;
  activeVideoOverlayLayers?: number;
  activeVideoOverlayCompositorPasses?: number;
  requestedVideoOverlayLayers?: number;
  droppedVideoOverlayLayers?: number;
  videoOverlayBudgetMode?: FxVideoOverlayBudgetMode;
  timelineSync?: TimelineSyncDiagnosticsSnapshot | null;
  nowMs?: number;
};

export class FxFpsTelemetry {
  private readonly scope: string;
  private emaFrameMs = 16.7;
  private lastEmitMs = 0;
  private longTaskBlockedMs = 0;
  private longTaskObserver: PerformanceObserver | null = null;

  constructor(scope: string) {
    this.scope = scope;
    this.initLongTaskObserver();
  }

  dispose(): void {
    if (!this.longTaskObserver) return;
    try {
      this.longTaskObserver.disconnect();
    } catch {}
    this.longTaskObserver = null;
  }

  sample(input: FxFpsTelemetrySample): void {
    const nowMs = Number.isFinite(input.nowMs) ? Number(input.nowMs) : now();
    // Clamp floor at 0.05ms (not 4ms) so the admin Performance HUD sees the
    // real sync-frame cost instead of a pegged "4ms" floor that hid sub-4ms
    // workloads and produced a nonsensical 250 "EMA FPS" when FX is idle.
    const frameMs = clamp(input.frameMs, 0.05, 120);
    this.emaFrameMs = this.emaFrameMs + (frameMs - this.emaFrameMs) * 0.18;

    if (nowMs - this.lastEmitMs < 1000) return;
    this.lastEmitMs = nowMs;

    const fps = 1000 / frameMs;
    const emaFps = 1000 / this.emaFrameMs;
    const frameBudgetOverrunMs = Math.max(0, frameMs - 16.667);
    const mainThreadBlockedMs = this.consumeLongTaskBlockedMs();
    const activeGpuOverlayTargets = Math.max(0, Math.round(Number(input.activeGpuOverlayTargets) || 0));
    const activeVideoOverlayLayers = Math.max(0, Math.round(Number(input.activeVideoOverlayLayers) || 0));
    const activeVideoOverlayCompositorPasses = Math.max(
      0,
      Math.round(Number(input.activeVideoOverlayCompositorPasses) || 0)
    );
    const requestedVideoOverlayLayers = Math.max(
      0,
      Math.round(Number(input.requestedVideoOverlayLayers) || 0)
    );
    const droppedVideoOverlayLayers = Math.max(
      0,
      Math.round(Number(input.droppedVideoOverlayLayers) || 0)
    );
    const videoOverlayBudgetMode =
      typeof input.videoOverlayBudgetMode === "string" ? input.videoOverlayBudgetMode : "full";

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("vb:fx-fps", {
          detail: {
            scope: this.scope,
            fps,
            emaFps,
            frameMs,
            emaFrameMs: this.emaFrameMs,
            frameBudgetOverrunMs,
            mainThreadBlockedMs,
            qualityTier: input.qualityTier,
            activeSegmentationTargets: input.activeSegmentationTargets,
            activeGpuOverlayTargets,
            activeVideoOverlayLayers,
            activeVideoOverlayCompositorPasses,
            requestedVideoOverlayLayers,
            droppedVideoOverlayLayers,
            videoOverlayBudgetMode,
            timelineSync: input.timelineSync ?? null,
            timestampMs: nowMs,
          },
        })
      );
    }

    if (!isFxFpsDebugEnabled()) return;
    console.info(
      `[fx-fps][${this.scope}] fps=${fps.toFixed(1)} ema=${emaFps.toFixed(1)} frameMs=${frameMs.toFixed(
        2
      )} tier=${input.qualityTier} seg=${input.activeSegmentationTargets} gpu=${activeGpuOverlayTargets} overlays=${activeVideoOverlayLayers}/${requestedVideoOverlayLayers} dropped=${droppedVideoOverlayLayers} budget=${videoOverlayBudgetMode} passes=${activeVideoOverlayCompositorPasses} longtask=${mainThreadBlockedMs.toFixed(
        1
      )}ms media=${input.timelineSync?.phases["media-sync"]?.emaMs.toFixed(1) ?? "-"} fx=${input.timelineSync?.phases["fx-compute"]?.emaMs.toFixed(1) ?? "-"} overlay=${input.timelineSync?.phases["overlay-application"]?.emaMs.toFixed(1) ?? "-"}`
    );
  }

  private initLongTaskObserver(): void {
    if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return;
    const supportedTypes = (PerformanceObserver as any).supportedEntryTypes as string[] | undefined;
    if (!Array.isArray(supportedTypes) || !supportedTypes.includes("longtask")) return;
    try {
      this.longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.longTaskBlockedMs += clamp(Number(entry.duration) || 0, 0, 10000);
        }
      });
      this.longTaskObserver.observe({ type: "longtask", buffered: true } as PerformanceObserverInit);
    } catch {
      this.longTaskObserver = null;
    }
  }

  private consumeLongTaskBlockedMs(): number {
    const blockedMs = this.longTaskBlockedMs;
    this.longTaskBlockedMs = 0;
    return blockedMs;
  }
}

function isFxFpsDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(DEBUG_KEY) === "1";
}

function now(): number {
  if (typeof performance !== "undefined") return performance.now();
  return Date.now();
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
